import React from 'react';
import Link from '../components/LocalizedLink';

import { ArrowRight, Utensils, Coffee, Pizza, Hotel, ShoppingBag, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { FadeInUp, StaggerContainer, StaggerItem } from '../components/AnimatedSection';

const PerChiPage = () => {
  const { t } = useTranslation();
  const audiences = [
    {
      icon: Utensils,
      title: t('per_chi.audiences.1_title'),
      desc: t('per_chi.audiences.1_desc'),
      benefit: t('per_chi.audiences.1_benefit'),
      color: "text-rose-500",
      bg: "bg-rose-50 dark:bg-rose-900/20"
    },
    {
      icon: Pizza,
      title: t('per_chi.audiences.2_title'),
      desc: t('per_chi.audiences.2_desc'),
      benefit: t('per_chi.audiences.2_benefit'),
      color: "text-orange-500",
      bg: "bg-orange-50 dark:bg-orange-900/20"
    },
    {
      icon: Coffee,
      title: t('per_chi.audiences.3_title'),
      desc: t('per_chi.audiences.3_desc'),
      benefit: t('per_chi.audiences.3_benefit'),
      color: "text-amber-600",
      bg: "bg-amber-50 dark:bg-amber-900/20"
    },
    {
      icon: Hotel,
      title: t('per_chi.audiences.4_title'),
      desc: t('per_chi.audiences.4_desc'),
      benefit: t('per_chi.audiences.4_benefit'),
      color: "text-sky-500",
      bg: "bg-sky-50 dark:bg-sky-900/20"
    },
    {
      icon: ShoppingBag,
      title: t('per_chi.audiences.5_title'),
      desc: t('per_chi.audiences.5_desc'),
      benefit: t('per_chi.audiences.5_benefit'),
      color: "text-violet-500",
      bg: "bg-violet-50 dark:bg-violet-900/20"
    }
  ];

  const guarantees = [
    { title: t('per_chi.guarantees.1_title'), desc: t('per_chi.guarantees.1_desc') },
    { title: t('per_chi.guarantees.2_title'), desc: t('per_chi.guarantees.2_desc') },
    { title: t('per_chi.guarantees.3_title'), desc: t('per_chi.guarantees.3_desc') },
    { title: t('per_chi.guarantees.4_title'), desc: t('per_chi.guarantees.4_desc') }
  ];

  return (
    <div data-testid="per-chi-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-[var(--color-primary)] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-60 h-60 bg-white/5 rounded-full" />
          <div className="absolute -bottom-10 -left-10 w-80 h-80 bg-white/5 rounded-full" />
          <div className="absolute top-1/2 left-1/4 w-2 h-2 bg-white/20 rounded-full" />
          <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-white/15 rounded-full" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
          <FadeInUp>
            <span className="text-white/80 uppercase text-sm font-semibold tracking-widest mb-4 inline-block">{t('per_chi.hero_tag')}</span>
          </FadeInUp>
          <FadeInUp delay={0.1}>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              {t('per_chi.hero_title')}
            </h1>
          </FadeInUp>
          <FadeInUp delay={0.2}>
            <p className="text-xl md:text-2xl text-white/80 max-w-2xl">
              {t('per_chi.hero_desc_1')}
              <strong className="text-white"> {t('per_chi.hero_desc_2')}</strong>
            </p>
          </FadeInUp>
        </div>
      </section>

      {/* Audience Grid */}
      <section className="py-16 md:py-20 bg-[var(--color-surface)]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" staggerDelay={0.1}>
            {audiences.map((audience, index) => (
              <StaggerItem key={index}>
                <div 
                  className="card-elevated p-6 h-full"
                  data-testid={`audience-${index}`}
                >
                  <div className={`w-14 h-14 rounded-2xl ${audience.bg} flex items-center justify-center mb-5`}>
                    <audience.icon className={`w-7 h-7 ${audience.color}`} />
                  </div>
                  <h3 className="text-xl font-bold text-[var(--color-text)] mb-2">
                    {audience.title}
                  </h3>
                  <p className="text-[var(--color-text-muted)] mb-4">
                    {audience.desc}
                  </p>
                  <p className="text-sm text-[var(--color-primary)] font-medium">
                    → {audience.benefit}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>

          <FadeInUp delay={0.3}>
            <p className="text-center text-[var(--color-text-muted)] mt-10">
              {t('per_chi.other_prefix')} <Link to="/contatti" className="text-[var(--color-primary)] font-semibold hover:underline">{t('per_chi.other_link')}</Link>{t('per_chi.other_suffix')}
            </p>
          </FadeInUp>
        </div>
      </section>

      {/* Garanzie */}
      <section className="py-16 md:py-20 bg-[var(--color-bg)]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <FadeInUp className="text-center mb-12">
            <span className="label-tag mb-2 inline-block text-sm">{t('per_chi.guarantees_tag')}</span>
            <h2 className="text-3xl font-bold text-[var(--color-text)]">
              {t('per_chi.guarantees_title')}
            </h2>
          </FadeInUp>
          
          <StaggerContainer className="grid sm:grid-cols-2 gap-6" staggerDelay={0.1}>
            {guarantees.map((item, index) => (
              <StaggerItem key={index}>
                <div 
                  className="flex items-start gap-4 p-5 highlight-box"
                  data-testid={`guarantee-${index}`}
                >
                  <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[var(--color-text)] mb-1">{item.title}</h3>
                    <p className="text-sm text-[var(--color-text-muted)]">{item.desc}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[var(--color-primary)] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-60 h-60 bg-white/5 rounded-full" />
          <div className="absolute -bottom-10 -right-10 w-80 h-80 bg-white/5 rounded-full" />
        </div>
        
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center relative z-10">
          <FadeInUp>
            <h2 className="text-3xl font-bold text-white mb-4">
              {t('per_chi.cta_title')}
            </h2>
          </FadeInUp>
          <FadeInUp delay={0.1}>
            <p className="text-lg text-white/80 mb-8">
              {t('per_chi.cta_desc')}
            </p>
          </FadeInUp>
          <FadeInUp delay={0.2}>
            <Link to="/tariffe">
              <Button 
                data-testid="cta-btn"
                className="bg-white text-[var(--color-primary)] hover:bg-white/90 rounded-full px-8 py-6 h-auto text-lg font-semibold"
              >
                {t('common.scopri_prezzi')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
};

export default PerChiPage;
